import React, { useCallback, useEffect } from "react";
import { Image, TouchableOpacity, View } from "react-native";
import Colors from "../../styles/colors";
import LogoNoText from "../../icons/LogoNoText";
import StandardText from "../../sharedComponents/standardText/StandardText";
import Spacing from "../../styles/spacing";
import PlayerBadge from "./PlayerBadge";
import CheckIcon from "../../icons/CheckIcon";
import GameHelper from "../GameHelper";
import Svg, { Defs, LinearGradient, Path, Stop } from "react-native-svg";
import InfoIcon from "../../icons/InfoIcon";
import blurredSuitsBg from "./../../../assets/blurred_suits_bg2.png";
import TutorialIcon from "../../icons/TutorialIcon";
import Tooltip, { TooltipStyles } from "../../sharedComponents/tooltip/Tooltip";
import LoadingButton from "../../sharedComponents/loadingButton/LoadingButton";
import { APP_VERSION, DEBUG } from "../../env.json";
import GameConfigIcons from "./GameConfigIcons";
import { secondsAsTimer, timerString, useOpenGameTimers } from "../../hooks/useOpenGameTimers";
import IconButton from "../../sharedComponents/iconButton/IconButton";
import ChatIcon from "../../icons/ChatIcon";
import { useHffeNavigation } from "../../hooks/useHffeNavigation";
import { fetchComputerPlayerPersonas } from "../../redux/viewer/computerPlayerSlice";
import { useFocusEffect } from "@react-navigation/native";
import { Menu, MenuOption, MenuOptions, MenuTrigger } from "react-native-popup-menu";
import Api from "../../services/Api";

const WaitingForPlayers = ({
    game = null,
    accessToken = null,
    userAccount = null,
    player = null,
    onRulesPress = null,
    onTutorialPress = null,
    onCancelPress = null,
    onAbandonSeat = null,
    onStartPress = null,
    onGoHomePress = null,
    gameError = null,
    hasUnreadChatMessages = false,
    dispatch = null,
    computerPlayerPersonas = null,
}) => {
    const hffeNavigation = useHffeNavigation();
    const { gameStartSeconds, gameExpiresSeconds } = useOpenGameTimers(game);
    const gameStartTimer = typeof gameStartSeconds === "number"
        ?  secondsAsTimer(gameStartSeconds, "minutes")
        : null;
    const gameExpiresTimer = typeof gameExpiresSeconds === "number"
        ? secondsAsTimer(gameExpiresSeconds, "hours")
        : null;

    useFocusEffect(useCallback(() => {
        if (userAccount && dispatch) {
            dispatch(fetchComputerPlayerPersonas());
        }
    }, [dispatch, userAccount]));

    return (
        <View style={{ ...ownStyles.container }}>
            <View style={{ position: "absolute", top: 15, left: 15, zIndex: 1 }}>

                {onRulesPress && (
                    <TouchableOpacity
                        style={{ ...ownStyles.rulesButton }}
                        onPress={onRulesPress}
                    >
                        <InfoIcon size={24} color={"rgba(70, 74, 83, 1)"} />
                        <StandardText style={{ ...ownStyles.rulesButtonText }}>
                            Rules
                        </StandardText>
                    </TouchableOpacity>
                )}

                {onTutorialPress && (
                        <TouchableOpacity
                        style={{ ...ownStyles.rulesButton, marginTop: 15 }}
                        onPress={onTutorialPress}
                    >
                        <TutorialIcon size={21} color={"rgba(70, 74, 83, 1)"} />
                        <StandardText style={{ ...ownStyles.rulesButtonText }}>
                            Tutorial
                        </StandardText>
                    </TouchableOpacity>
                )}
            </View>

            <View style={{ position: "absolute", top: 15, right: 15, zIndex: 1 }}>
                <TouchableOpacity onPress={onCancelPress}>
                    <StandardText style={{ fontSize: 14 }}>Cancel</StandardText>
                </TouchableOpacity>
            </View>

            <View style={{ ...ownStyles.blurredBackground }}>
                <Image
                    source={{ uri: blurredSuitsBg }}
                    style={{ width: 526, height: 923 }}
                />
            </View>

            <View>
                <LogoNoText size={156} />
            </View>

            <View style={{ ...ownStyles.idContainer }}>
                <StandardText style={{ ...ownStyles.idText }}>
                    Game ID: {game?.id ?? 'N/A'} {DEBUG && <> / {APP_VERSION}</>}
                </StandardText>
            </View>

            {gameError && (
                <>
                    <View style={{ ...ownStyles.waitingContainer }}>
                        <StandardText style={{ ...ownStyles.waitingText }}>
                            {gameError}
                        </StandardText>
                    </View>

                    <View style={{ marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <LoadingButton
                            label="Go Home"
                            paddingRight={32}
                            chevronRight={8}
                            showChevron={true}
                            onPress={onGoHomePress}
                        />
                    </View>
                </>
            )}

            {game && !game.expired && !game.abandoned && (<>

                {game.isOpenGame && !game.abandoned && !game.expired && (
                    <View style={{ ...ownStyles.waitingContainer }}>
                        <StandardText style={{ ...ownStyles.waitingText }}>
                            {`Waiting for additional players.\nOnce all players join the game will start automatically.`}
                        </StandardText>
                    </View>
                )}

                {!game.isOpenGame && (
                    <View style={{ ...ownStyles.waitingContainer }}>
                            <StandardText style={{ ...ownStyles.waitingText }}>
                                {`You're all set!\nWaiting for players...`}
                            </StandardText>
                    </View>
                )}

                <GameConfigIcons game={game} style={{ marginTop: 8, marginBottom: 8 }} iconColor={Colors.onBackground} enableTooltips={true} />

                <View>
                    <View style={{ flexDirection: "row", justifyContent: "center" }}>
                        {
                            <View
                                style={{
                                    flexDirection: "row",
                                    margin: Spacing.extraLarge,
                                    alignItems: "flex-start",
                                }}
                            >
                                {renderTeamPlayers({
                                    team: GameHelper.getViewerTeam(game),
                                    teamIndex: 0,
                                    openSeats: GameHelper.getViewerTeamOpenSeats(game),
                                    game,
                                    computerPlayerPersonas,
                                    accessToken,
                                })}
                                <View style={{ ...ownStyles.vsContainer }}>
                                    <View style={{ alignItems: "center" }}>
                                        <View>
                                            <Svg width="2" height="62" viewBox="0 0 2 62" fill="none">
                                                <Path
                                                    opacity="0.4"
                                                    d="M1 0.5V62"
                                                    stroke="url(#paint0_linear)"
                                                />
                                                <Defs>
                                                    <LinearGradient
                                                        id="paint0_linear"
                                                        x1="1.99997"
                                                        y1="1.87778e-08"
                                                        x2="1.99997"
                                                        y2="62"
                                                        gradientUnits="userSpaceOnUse"
                                                    >
                                                        <Stop stopColor="white" />
                                                        <Stop offset="0.567708" stopColor="#11204A" />
                                                        <Stop
                                                            offset="1"
                                                            stopColor="#11204A"
                                                            stopOpacity="0"
                                                        />
                                                    </LinearGradient>
                                                </Defs>
                                            </Svg>
                                        </View>
                                        <StandardText
                                            style={{
                                                marginTop: -42,
                                                backgroundColor: Colors.background,
                                                zIndex: 1,
                                            }}
                                        >
                                            vs
                                        </StandardText>
                                    </View>
                                </View>
                                {renderTeamPlayers({
                                    team: GameHelper.getOpponentTeam(game),
                                    teamIndex: 1,
                                    openSeats: GameHelper.getOppnentTeamOpenSeats(game),
                                    game,
                                    computerPlayerPersonas,
                                    accessToken,
                                })}
                            </View>
                        }
                    </View>
                    {game && (
                        <View style={{ marginLeft: 2, position: 'relative', alignSelf: 'center' }}>
                            <IconButton icon={<ChatIcon size={24} color={Colors.onBackground} />} onPress={() => {hffeNavigation.gotoMessages({ game, hasUnreadChatMessages })}} />
                            {hasUnreadChatMessages && (<View style={{ position: "absolute", top: 4, right: 4, backgroundColor: Colors.secondary, borderRadius: 32, width: 8, height: 8, justifyContent: 'center', alignItems: 'center'}} />)}
                        </View>
                    )}

                    {/* {gameStartTimer && (
                        <View style={{ marginTop: 16, textAlign: "center" }}>
                            <StandardText style={{ fontSize: 18, color: Colors.onBackground, fontWeight: 500 }}>
                                {timerString([gameStartTimer.minutes, gameStartTimer.seconds])} left
                            </StandardText>
                        </View>
                    )} */}

                    {gameExpiresTimer && !gameStartTimer && (
                        <View style={{ marginTop: 16, textAlign: "center" }}>
                            <StandardText style={{ fontSize: 18, color: Colors.onBackground, fontWeight: 500 }}>
                                {timerString([gameExpiresTimer.hours, gameExpiresTimer.minutes, gameExpiresTimer.seconds])} left before game expires
                            </StandardText>
                        </View>
                    )}

                    {game.isOpenGame && game.isAllSeatsFilled && !player.ready && (
                        <View style={{ marginTop: 16 }}>
                            <LoadingButton
                                label="Start Game"
                                showChevron={true}
                                maxWidth={"100%"}
                                onPress={onStartPress}
                            />
                        </View>
                    )}

                    {game.isOpenGame && (!game.isAllSeatsFilled || player.ready) && (
                        <View style={{ marginTop: 16 }}>
                            <LoadingButton
                                label="Abandon Seat"
                                showChevron={true}
                                maxWidth={"100%"}
                                onPress={onAbandonSeat}
                            />
                        </View>
                    )}
                </View>
            </>)}
        </View>
    );
};

const ownStyles = {
    container: {
        width: "100%",
        height: "100%",
        backgroundColor: Colors.background,
        justifyContent: "center",
        alignItems: "center",
    },
    blurredBackground: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingTop: 200,
    },
    idContainer: {
        borderRadius: 4,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 16,
        paddingRight: 16,
        background: "linear-gradient(90deg, #8850FF 0%, #E67E5D 100%)",
        marginTop: -35,
    },
    idText: {
        color: Colors.onPrimary,
        fontSize: 20,
        weight: 500,
    },
    waitingContainer: {
        marginTop: Spacing.large,
        marginBottom: Spacing.extraLarge,
    },
    waitingText: {
        fontSize: 24,
        weight: 500,
        textAlign: "center",
        lineHeight: 32,
    },
    joinedCheckContainer: {
        width: 24,
        height: 24,
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        bottom: -2,
        right: -2,
        padding: 2,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: Colors.success,
        backgroundColor: Colors.background,
    },
    tutorialIconContainer: {
        width: 26,
        height: 26,
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        bottom: -2,
        right: -2,
        padding: 2,
        backgroundColor: "transparent",
    },
    vsContainer: {
        marginTop: 0,
        marginStart: Spacing.large,
        marginEnd: Spacing.large,
    },
    rulesButton: {
        flexDirection: "row",
        borderWidth: 1,
        borderRadius: 5,
        borderColor: "rgb(190,190,190)",
        padding: 8,
        alignItems: "center",
        backgroundColor: Colors.background,
    },
    rulesButtonText: {
        marginStart: 5,
    },
    playerBadge: {
        width: 48,
        height: 48,
        borderRadius: 100,
        borderWidth: 2,
        borderColor: "white",
        justifyContent: "center",
        alignItems: "center",
        marginTop: 4,
    },
    menuOptions: {
        optionsContainer: {
            backgroundColor: 'transparent',
            marginTop: 45,
            boxShadow: 'none'
        }
    },
    menuOption: {
        optionWrapper: {
            backgroundColor: Colors.background,
            marginBottom: -2,
            borderRadius: 3,
            borderWidth: 2,
            padding: 10,
            borderColor: Colors.onBackground,
            boxShadow: 'rgb(0 0 0 / 20%) 3px 3px 4px'
        },
        optionText: {
            color: Colors.onBackground,
        }
    },
};

const getTeamColor = (teamIndex) => {
    switch (teamIndex) {
        case 0:
            return "rgba(94, 143, 255, 1)";
        case 1:
            return "rgba(237, 102, 99, 1)";
        case 2:
            return "rgba(17, 32, 74, 1)";
    }
};

const renderBadge = ({player, teamIndex, game, computerPlayerPersonas, accessToken, team}) => {
    if (!player) {
        const handleAddPlayerToOpenSeat = (id) => {
            Api.addPlayerToOpenSeat(accessToken, -1 * id, Number.parseInt(game.id, 10), Number.parseInt(team.id, 10));
        };

        return (
            <Menu>
                <MenuTrigger disabled={!computerPlayerPersonas || !game.isCreator}>
                    <View
                        style={{
                            ...ownStyles.playerBadge,
                            backgroundColor: "transparent",
                            borderColor: getTeamColor(teamIndex),
                        }}
                    >
                        <StandardText
                            style={{
                                color: getTeamColor(teamIndex),
                                weight: 500,
                            }}
                        >
                            +
                        </StandardText>
                    </View>
                </MenuTrigger>
                <MenuOptions customStyles={{
                    ...ownStyles.menuOptions,
                    optionsContainer: { backgroundColor: 'transparent', marginTop: -50, boxShadow: 'none' }
                }}>
                    {computerPlayerPersonas && game.isCreator ? computerPlayerPersonas.map((persona) => (
                        <MenuOption key={persona.id} customStyles={ownStyles.menuOption} onSelect={() => {
                            handleAddPlayerToOpenSeat(persona.id);
                        }} text={persona.name} />
                    )) : null}
                </MenuOptions>
            </Menu>
        );
    }

    const firstName =
        player.userAccount?.user?.firstName || player.firstName || "(??)";
    const name =
        firstName.length > 6 ? firstName.substring(0, 6) + "..." : firstName;

    return (
        <View>
            <View style={{ margin: Spacing.small }}>
                <PlayerBadge
                    player={player}
                    bare={true}
                    size={48}
                    fontSize={20}
                    gapSize={3}
                    activePlainColor={getTeamColor(teamIndex)}
                    inactivePlainColor={getTeamColor(teamIndex)}
                    showBackgroundOnActive={true}
                    showBackgroundOnInactive={false}
                    active={!player.joinedGame}
                    popupEnabled={true}
                    game={game}
                    presenceIndicatorLocation="top-left"
                />
                {player.joinedGame && player.ready && (
                    <View style={{ ...ownStyles.joinedCheckContainer }}>
                        <CheckIcon color={Colors.success} size={12} />
                    </View>
                )}
            </View>
            <View style={{ marginTop: Spacing.small }}>
                <StandardText
                    style={{
                        color: "rgba(177,182,196,1)",
                        fontSize: 14,
                        textAlign: "center",
                    }}
                >
                    {name}
                </StandardText>
            </View>
        </View>
    );
};

const renderTeamPlayers = ({team, teamIndex, openSeats, game, computerPlayerPersonas, accessToken}) => {
    const badges = team.teamPlayers
        .slice()
        .sort((a, b) => a.seat - b.seat)
        .map((player) => {
            const firstName = player.userAccount?.user?.firstName || player.firstName || "(??)";
            const username = player.userAccount?.username || "";
            const displayName = firstName + (username.length > 0 ? " (" + username + ")" : "") + " ";
            let tutorialText = null;

            if (!player.joinedGame) {
                tutorialText = <StandardText style={{ ...TooltipStyles.tooltipText }}>
                        <StandardText style={{ ...TooltipStyles.tooltipText, fontWeight: 700 }}>{displayName}</StandardText>
                        has not joined yet.
                    </StandardText>;
            } else if (player.joinedGame && !player.ready) {
                tutorialText = <StandardText style={{ ...TooltipStyles.tooltipText }}>
                        <StandardText style={{ ...TooltipStyles.tooltipText, fontWeight: 700 }}>{displayName}</StandardText>
                        is not ready.
                    </StandardText>;
            } else if (player.joinedGame && player.ready) {
                tutorialText = <StandardText style={{ ...TooltipStyles.tooltipText }}>
                        <StandardText style={{ ...TooltipStyles.tooltipText, fontWeight: 700 }}>{displayName}</StandardText>
                        is ready to play!
                    </StandardText>;
            }

            return (
                <View style={{ zIndex: 2 }} key={player.id}>
                    {tutorialText ? (
                        <Tooltip
                            children={renderBadge({player, teamIndex, game, team})}
                            content={tutorialText}
                            customWidth={115}
                        />
                    ) : (
                        renderBadge({player, teamIndex, game, team})
                    )}
                </View>
            );
        });
        
    badges.push(
        ...openSeats.map((_, idx) => (
            <View style={{ zIndex: 2 }} key={"open-seat-" + idx}>
                <Tooltip
                    children={renderBadge({ player: null, teamIndex, game, computerPlayerPersonas, accessToken, team})}
                    content={"Seat is open"}
                    customWidth={115}
                />
            </View>
        ))
    );

    return badges;
};

export default WaitingForPlayers;
